import { createPortal } from 'react-dom';
import type { AnalysisResult } from '@/core/analysis/types';
import { chartBars, chartSummary, type SongInfo } from '@/core/export/chart';
import { pianoKeys, TUNINGS, type Instrument } from '@/core/instruments/shapes';
import type { Chord } from '@/core/theory/chord';
import { chordName, type DisplayOptions } from '@/core/theory/display';
import {
  FretDiagram,
  layoutFor,
  PianoDiagram,
  STRING_SPACING,
  useVoicingPrefs,
} from '@/features/chord-diagrams';
import styles from './PrintSheet.module.css';

interface PrintSheetProps {
  song: SongInfo;
  result: AnalysisResult;
  options: DisplayOptions;
  instrument: Instrument;
  leftHanded: boolean;
}

/**
 * The printed chord sheet (F8.1): title, key and capo, the chart in bars, and a diagram of every
 * chord used. Hidden on screen; when printing, it is the only thing on the page.
 */
export function PrintSheet({ song, result, options, instrument, leftHanded }: PrintSheetProps) {
  const prefs = useVoicingPrefs();
  const bars = chartBars(result, options);
  const chords = new Map<string, Chord>();
  for (const seg of result.segments) {
    if (!seg.chord) continue;
    const name = chordName(seg.chord, options);
    if (!chords.has(name)) chords.set(name, seg.chord);
  }
  const fretted = instrument === 'piano' ? null : instrument;

  return createPortal(
    <article className={`kyc-print-sheet ${styles.sheet}`} aria-hidden="true">
      <header className={styles.header}>
        <h1 className={styles.title}>{song.title}</h1>
        {song.artist && <p className={styles.artist}>{song.artist}</p>}
        <p className={styles.summary}>{chartSummary(result, options)}</p>
      </header>

      <div className={styles.chart}>
        {bars.map((bar) => (
          <div key={bar.number} className={styles.bar}>
            {bar.chords.length ? bar.chords.join('  ') : '%'}
          </div>
        ))}
      </div>

      <section className={styles.diagrams}>
        {[...chords.entries()].map(([name, chord]) => (
          <figure key={name} className={styles.diagram}>
            <figcaption className={styles.name}>{name}</figcaption>
            {fretted ? (
              <FretDiagram
                layout={layoutFor(chord, fretted, options, leftHanded, prefs)}
                strings={TUNINGS[fretted].length}
                spacing={STRING_SPACING[fretted]}
              />
            ) : (
              <PianoDiagram keys={pianoKeys(chord, options.transpose)} />
            )}
          </figure>
        ))}
      </section>

      <footer className={styles.footer}>Chords detected by KnowYourChord</footer>
    </article>,
    document.body,
  );
}
